// Promos vigentes de la tienda arriba del grid, en hasta tres cajas: «En venta», «Sobre accesorios» y
// «En crédito». Recibe las promos ya clasificadas; una caja sin promos no se pinta.
import type { LucideIcon } from 'lucide-react';
import { CreditCard, Package, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Una promo de la caja; `customerText` va en el tooltip. */
export interface StorePromoLine {
    key: string;
    name: string;
    customerText?: string | null;
}

const TONE = {
    success: { box: 'border-success/30 bg-success-soft/60', head: 'text-success' },
    info: { box: 'border-info/30 bg-info-soft/60', head: 'text-info' },
    promo: { box: 'border-promo/30 bg-promo-soft/60', head: 'text-promo' },
} as const;

const COLUMNS = ['', 'md:grid-cols-1', 'md:grid-cols-2', 'md:grid-cols-3'] as const;

const byName = (a: StorePromoLine, b: StorePromoLine): number =>
    a.name.localeCompare(b.name, 'es-MX');

function PromoBox({
    label,
    icon: Icon,
    tone,
    lines,
}: {
    label: string;
    icon: LucideIcon;
    tone: keyof typeof TONE;
    lines: readonly StorePromoLine[];
}) {
    const styles = TONE[tone];
    return (
        <section
            aria-label={label}
            className={cn('min-w-0 rounded-md border px-3 py-2 text-[12.5px]', styles.box)}
        >
            <h2
                className={cn(
                    'mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide',
                    styles.head,
                )}
            >
                <Icon aria-hidden className="size-3.5" />
                {label}
                <span className="font-normal opacity-70">({lines.length})</span>
            </h2>
            <ul className="flex flex-col gap-0.5">
                {[...lines].sort(byName).map((line) => (
                    <li key={line.key} className="truncate" title={line.customerText ?? line.name}>
                        · {line.name}
                    </li>
                ))}
            </ul>
        </section>
    );
}

export function StorePromosBanner({
    sale,
    accessories,
    credit,
}: {
    /** Promos de venta de toda la tienda que tocan el equipo. */
    sale: readonly StorePromoLine[];
    /** Promos de venta que solo cambian el precio de los accesorios. */
    accessories: readonly StorePromoLine[];
    /** Promos de crédito de toda la tienda. */
    credit: readonly StorePromoLine[];
}) {
    const boxes = [
        sale.length > 0 && (
            <PromoBox key="sale" label="En venta" icon={Sparkles} tone="success" lines={sale} />
        ),
        accessories.length > 0 && (
            <PromoBox
                key="accessories"
                label="Sobre accesorios"
                icon={Package}
                tone="info"
                lines={accessories}
            />
        ),
        credit.length > 0 && (
            <PromoBox
                key="credit"
                label="En crédito"
                icon={CreditCard}
                tone="promo"
                lines={credit}
            />
        ),
    ].filter(Boolean);
    if (boxes.length === 0) return null;
    return (
        <div
            data-testid="store-promos-banner"
            className={cn('mb-3 grid grid-cols-1 gap-2', COLUMNS[boxes.length])}
        >
            {boxes}
        </div>
    );
}
